/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnDestroy, signal,
} from '@angular/core';
import { DialogService } from '@ng-falcon/dialog/dialog.service';
import { CommunityProfileEditDialogComponent, CommunityProfileEditMode } from '../edit-dialog/community-profile-edit-dialog.component';
import { CommunityProfileMentionDialogComponent } from '../mention-dialog/community-profile-mention-dialog.component';

const COPY_FEEDBACK_MS = 2000;
// UserService.getProfileBanner's fallback when there's no upload (ProfileCtrl also fills it in).
const STOCK_BANNER = '/utest-static/assets/images/profile-cover-bg.jpg';

// Cover, avatar, identity (with your referral link on your own profile), actions and stat cards.
@Component({
  selector: 'ut-community-profile-header',
  templateUrl: './community-profile-header.component.html',
  styleUrls: ['./community-profile-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityProfileHeaderComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) user: any;
  @Input() me: any;
  @Input() isOwn = false;
  @Input() testerAppUrl = '';
  @Input() utestUrl = '';
  @Input() followingCount = 0;

  banner = '';
  isStockBanner = false;
  copied = signal(false);
  private copiedTimer: ReturnType<typeof setTimeout> | undefined;
  role = '';
  linkedIn: string | null = null;

  constructor(
    @Inject('UserService') private UserService: any,
    @Inject('AlertService') private AlertService: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
    @Inject('$filter') private $filter: any,
    private dialogService: DialogService,
  ) {}

  ngOnChanges(): void {
    // The legacy page's stock photo when there's no upload.
    this.banner = this.UserService.getProfileBanner(this.user).url;
    this.isStockBanner = this.banner === STOCK_BANNER;
    this.role = this.UserService.userRoleTitle(this.user);
    const linkedIn = this.user.social_network_urls?.linked_in;
    this.linkedIn = linkedIn ? this.$filter('prependHttp')(linkedIn) : null;
  }

  get canMention(): boolean {
    return !this.isOwn && !!this.me?.current_user_permissions?.create_status;
  }

  get canFollow(): boolean {
    return !this.isOwn && !!this.user.current_user_permissions?.follow;
  }

  get showRank(): boolean {
    return !!this.user.rank && !this.user.is_employee;
  }

  get editProfileLink(): string {
    // Account Settings holds About me, the avatar and the banner; My Profile has none of them.
    return `${this.testerAppUrl}/account/settings`;
  }

  get referralLink(): string {
    return `${this.utestUrl}/ref${this.me?.platform_id}`;
  }

  mention(): void {
    if (this.LightTesterErrorService.show()) return;
    this.dialogService.open(CommunityProfileMentionDialogComponent, { data: { user: this.user } });
  }

  edit(mode: CommunityProfileEditMode): void {
    this.dialogService.open(CommunityProfileEditDialogComponent, { data: { mode } });
  }

  // Same feedback as the header's Copy ID: the icon turns into a green check for a moment.
  copyReferral(): void {
    navigator.clipboard.writeText(this.referralLink).then(() => {
      this.copied.set(true);
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => this.copied.set(false), COPY_FEEDBACK_MS);
    }, () => this.AlertService.addDanger('Couldn\'t copy the link'));
  }

  ngOnDestroy(): void {
    clearTimeout(this.copiedTimer);
  }
}
